import { useRef, useState } from 'react';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import firebaseStorage from '@/firebase/storage';
import { customToast } from '@/components/toast/custom-react-toastify/v1';
import { CustomBackdropController } from '@/components/backdrop/default/v1';
import { useUserPageContextValueHook } from '@/contexts/UserPageContextProvider';
import { useLinkCategoryContextValueHook } from '@/views/mypage/contexts/LinkCategoryContextProvider';
import { useQueryParamsHook } from '@/hooks/useQueryParamsHook';
import { CustomRegexUtils } from '@/utils/CustomRegexUtils';
import { Switch } from '@mui/material';
import { IMAGE_MODULE_THEMES, resolveDisplayConfig } from './imageThemes';
import { TEXT_FONTS, TextStyleFields } from '../weget-text-item/textStyles';
import { ALBUM_LAYOUTS, ALBUM_PLACEHOLDERS, CAROUSEL_MAX_IMAGES, CAROUSEL_RATIOS, findAlbumLayout } from './albumLayouts';

const customRegexUtils = CustomRegexUtils();
const MAX_FILE_SIZE = 5 * 1024 * 1024;

// 앨범 빈 칸 채움 (사진이 모자랄 때 그라데이션으로 칸을 채워 저장)
const fillerImage = (index) => ({ placeholder: index % ALBUM_PLACEHOLDERS.length });
const isFiller = (image) => image?.placeholder != null;

/** 빈 칸 채움 그림 (공개 페이지 ImageModule 의 PlaceholderTile 과 같은 모양) */
export function PlaceholderTile({ placeholder }) {
    return (
        <span aria-hidden="true" className="absolute inset-0 overflow-hidden" style={{ background: ALBUM_PLACEHOLDERS[placeholder % ALBUM_PLACEHOLDERS.length] }}>
            <span className="absolute -right-[12%] -top-[12%] aspect-square w-1/2 rounded-full bg-white/35" />
            <span className="absolute -bottom-[30%] -left-[15%] aspect-square w-3/4 rounded-full bg-white/25" />
        </span>
    );
}

// 설명 폰트: '테마 기본'(일기장 손글씨, 기본 테마는 기본 폰트) + 텍스트 모듈 6종 ('기본'은 '테마 기본'과 헷갈리지 않게 '고딕')
const DESC_FONTS = [{ value: null, label: '테마 기본', family: null }, ...TEXT_FONTS];

const SEGMENT = (active) => `rounded-lg px-3 py-2 text-[13px] font-semibold ring-1 transition ${active ? 'bg-gray-900 text-white ring-gray-900' : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'}`;

/** 앞/뒤 사진과 자리 바꾸기 */
export function MoveButtons({ index, last, onMove }) {
    return (
        <div className="pointer-events-none absolute inset-x-1 bottom-1 flex justify-between">
            <button type="button" onClick={() => onMove(index, index - 1)} disabled={index === 0} aria-label={`${index + 1}번 이미지 앞으로`} className="pointer-events-auto h-6 w-6 rounded-full bg-white/90 text-xs shadow disabled:invisible">◀</button>
            <button type="button" onClick={() => onMove(index, index + 1)} disabled={index === last} aria-label={`${index + 1}번 이미지 뒤로`} className="pointer-events-auto h-6 w-6 rounded-full bg-white/90 text-xs shadow disabled:invisible">▶</button>
        </div>
    );
}

/** 배치 모양 미리보기 (번호 = 이미지 순서) */
function LayoutDiagram({ layout, images = [], onSlotClick, onRemove, onMove, dragProps, size = 'sm' }) {
    const slots = Array.from({ length: layout.slotCount }, (_, i) => i);

    return (
        <div className={`${layout.container} ${size === 'sm' ? 'gap-[2px]' : 'gap-1'}`}>
            {slots.map(i => {
                const image = images[i];
                const cellClass = layout.cells?.[i] || layout.cell;
                if (size === 'sm') {
                    return <span key={i} className={`flex items-center justify-center rounded-[3px] bg-gray-300 text-[9px] font-bold text-white ${cellClass} ${cellClass ? '' : 'min-h-[10px]'}`}>{i + 1}</span>;
                }
                return (
                    <div key={i} {...dragProps(i, !!image)} className={`group relative overflow-hidden rounded-md bg-gray-100 ${image ? 'cursor-grab' : ''} ${cellClass} ${cellClass ? '' : 'min-h-[40px]'}`}>
                        {image
                            ? <>
                                {isFiller(image)
                                    ? <PlaceholderTile placeholder={image.placeholder} />
                                    : <img src={image.url} alt="" draggable={false} className="absolute inset-0 h-full w-full object-cover" />
                                }
                                {isFiller(image) &&
                                    <button type="button" onClick={() => onSlotClick(i)} className="absolute inset-0 flex items-end justify-center pb-2 hover:bg-black/10">
                                        <span className="rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold text-gray-700 shadow">눌러서 사진 넣기</span>
                                    </button>
                                }
                                <button type="button" onClick={() => onRemove(i)} aria-label={`${i + 1}번 이미지 삭제`} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white">✕</button>
                                <span className="absolute left-1 top-1 rounded bg-black/50 px-1.5 text-[11px] font-bold text-white">{i + 1}</span>
                                <MoveButtons index={i} last={layout.slotCount - 1} onMove={onMove} />
                            </>
                            : <button type="button" onClick={() => onSlotClick(i)} className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600">
                                <span className="text-xl leading-none">+</span>
                                <span className="text-[11px] font-semibold">{i + 1}</span>
                            </button>
                        }
                    </div>
                );
            })}
        </div>
    );
}

/**
 * 캐러셀·앨범 모듈 입력 폼 (추가·수정 공용).
 * onSubmit 에는 서버 link-items/create, update/info 에 보낼 body(id 제외)를 넘긴다.
 */
export function WegetImageModuleForm({ type, initial, submitLabel, onSubmit }) {
    const userPageContextValueHook = useUserPageContextValueHook();
    const linkCategoryContextValueHook = useLinkCategoryContextValueHook();
    const lcId = useQueryParamsHook()?.params?.lcId;
    const fileInputRef = useRef(null);
    const uploadTargetRef = useRef(null); // 앨범: 채울 칸 번호, 캐러셀: null(뒤에 추가)

    const isCarousel = type === 'CAROUSEL';
    const categories = linkCategoryContextValueHook?.content || [];

    const [title, setTitle] = useState(initial?.title || '');
    const [linkUrl, setLinkUrl] = useState(initial?.linkUrl || '');
    const [description, setDescription] = useState(initial?.description || '');
    const [showDescription, setShowDescription] = useState(resolveDisplayConfig(initial).showDescription);
    const [theme, setTheme] = useState(resolveDisplayConfig(initial).theme);
    // 설명 꾸미기 (텍스트 모듈과 같은 값). font·color null = 테마 기본 (일기장은 손글씨)
    const [descStyle, setDescStyle] = useState({
        font: initial?.displayConfig?.font || null,
        align: initial?.displayConfig?.align || 'LEFT',
        color: initial?.displayConfig?.color || null,
    });
    const [images, setImages] = useState(initial?.imageList || []);
    const [ratio, setRatio] = useState(initial?.carouselConfig?.ratio || '1:1');
    const [count, setCount] = useState(initial?.albumConfig?.count || 4);
    const [layoutValue, setLayoutValue] = useState(initial?.albumConfig?.layout || 'GRID');
    const [linkCategoryId, setLinkCategoryId] = useState(
        categories.find(r => r.id === (initial ? initial.linkCategoryId : lcId))?.id || ''
    );

    const layout = { ...findAlbumLayout(count, layoutValue), slotCount: count };
    const albumImages = images.slice(0, count);

    const handleChangeCount = (next) => {
        setCount(next);
        setLayoutValue(ALBUM_LAYOUTS[next][0].value);
        // 장수를 줄여도 뒤 사진은 버리지 않는다 (다시 늘리면 복원, 저장할 때만 count 장으로 자름)
    }

    const openFilePicker = (slotIndex = null) => {
        uploadTargetRef.current = slotIndex;
        fileInputRef.current.value = '';
        fileInputRef.current.click();
    }

    const uploadFiles = async (files) => {
        const tooBig = files.find(f => f.size > MAX_FILE_SIZE);
        if (tooBig) {
            customToast.error('이미지 크기는 최대 5MB까지만 허용됩니다.');
            return [];
        }

        CustomBackdropController().showBackdrop();
        try {
            return await Promise.all(files.map(async file => {
                const extension = file.name.split('.').pop();
                const fileRef = ref(firebaseStorage, `/assets/user_pages/${userPageContextValueHook?.userPage?.id}/module_images/${uuidv4()}.${extension}`);
                const snapshot = await uploadBytes(fileRef, file);
                return { url: await getDownloadURL(snapshot.ref) };
            }));
        } catch (error) {
            customToast.error('이미지 업로드에 실패했습니다. 다시 시도해 주세요.');
            return [];
        } finally {
            CustomBackdropController().hideBackdrop();
        }
    }

    const handleSelectFiles = async (e) => {
        const files = [...e.target.files].filter(f => f.type.startsWith('image/'));
        if (!files.length) {
            return;
        }

        const slot = uploadTargetRef.current;

        if (isCarousel) {
            const room = CAROUSEL_MAX_IMAGES - images.length;
            if (files.length > room) {
                customToast.error(`캐러셀은 최대 ${CAROUSEL_MAX_IMAGES}장까지 등록할 수 있어요.`);
            }
            const uploaded = await uploadFiles(files.slice(0, room));
            setImages(prev => [...prev, ...uploaded].slice(0, CAROUSEL_MAX_IMAGES));
            return;
        }

        // 앨범: 고른 칸부터 빈 칸을 순서대로 채운다
        // (채움 칸도 빈 칸으로 보고 사진으로 교체)
        const isOpen = (i) => !images[i] || isFiller(images[i]);
        const emptySlots = Array.from({ length: count }, (_, i) => i).filter(i => isOpen(i) && i >= slot)
            .concat(Array.from({ length: count }, (_, i) => i).filter(i => isOpen(i) && i < slot));
        const uploaded = await uploadFiles(files.slice(0, emptySlots.length));
        setImages(prev => {
            const next = [...prev];
            uploaded.forEach((image, k) => { next[emptySlots[k]] = image; });
            return next;
        });
    }

    // 빈 칸만 그라데이션으로 채운다 (올린 사진은 그대로)
    const fillSamples = () => {
        setImages(prev => Array.from({ length: Math.max(count, prev.length) }, (_, i) => prev[i] || (i < count ? fillerImage(i) : undefined)));
    }

    const removeImage = (index) => {
        setImages(prev => isCarousel ? prev.filter((_, i) => i !== index) : prev.map((image, i) => (i === index ? undefined : image)));
    }

    // 순서 바꾸기: 캐러셀은 끼워 넣기, 앨범은 칸끼리 맞바꾸기 (빈 칸으로 옮기기 포함)
    const moveImage = (from, to) => {
        if (from === null || from === to) {
            return;
        }
        setImages(prev => {
            const next = [...prev];
            if (isCarousel) {
                next.splice(to, 0, next.splice(from, 1)[0]);
            } else {
                [next[from], next[to]] = [next[to], next[from]];
            }
            return next;
        });
    }

    // PC: 사진을 끌어다 다른 칸에 놓기 (모바일은 ◀▶ 버튼)
    const dragFromRef = useRef(null);
    const dragProps = (i, draggable) => ({
        draggable,
        onDragStart: (e) => {
            dragFromRef.current = i;
            // Firefox 는 dataTransfer 에 값을 넣어야 끌기가 시작된다
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', String(i));
        },
        onDragOver: (e) => e.preventDefault(),
        onDrop: (e) => { e.preventDefault(); moveImage(dragFromRef.current, i); dragFromRef.current = null; },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const filled = (isCarousel ? images : albumImages).filter(Boolean);

        if (isCarousel && filled.length < 1) {
            customToast.error('이미지를 1장 이상 등록해 주세요.');
            return;
        }
        if (!isCarousel && !filled.some(image => !isFiller(image))) {
            customToast.error('사진을 1장 이상 넣어 주세요.');
            return;
        }
        if (!isCarousel && filled.length !== count) {
            customToast.error(`앨범 이미지 ${count}장을 모두 채워 주세요.`);
            return;
        }
        if (linkUrl.trim() && !customRegexUtils.isValidUrlFormat(linkUrl.trim())) {
            customToast.error('올바른 URL을 입력해 주세요. \nex) https://www.example.com');
            return;
        }

        onSubmit({
            type,
            title: title.trim(),
            description: description.trim(), // 꺼도 내용은 저장해 두고 다시 켜면 복원
            displayConfig: { showDescription, theme, ...descStyle },
            linkUrl: linkUrl.trim(),
            imageList: filled,
            ...(isCarousel ? { carouselConfig: { ratio } } : { albumConfig: { count, layout: layoutValue } }),
            linkCategoryId: linkCategoryId || '',
            userPageId: userPageContextValueHook?.userPage?.id,
        });
    }

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5 px-1 pb-2">
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleSelectFiles} />

            {isCarousel
                ? <section className="flex flex-col gap-3">
                    <div>
                        <p className="text-sm font-bold">비율</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {CAROUSEL_RATIOS.map(r => (
                                <button key={r.value} type="button" aria-pressed={ratio === r.value} onClick={() => setRatio(r.value)} className={SEGMENT(ratio === r.value)}>{r.label}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="text-sm font-bold">이미지 <span className="font-normal text-gray-400">{images.length}/{CAROUSEL_MAX_IMAGES} · 왼쪽부터 순서대로 넘어가요 · 끌어서 또는 ◀▶ 로 순서 변경</span></p>
                        <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                            {images.map((image, i) => (
                                <div key={`${image.url}-${i}`} {...dragProps(i, true)} className={`relative cursor-grab overflow-hidden rounded-lg bg-gray-100 ${CAROUSEL_RATIOS.find(r => r.value === ratio)?.aspect}`}>
                                    <img src={image.url} alt="" draggable={false} className="absolute inset-0 h-full w-full object-cover" />
                                    <span className="absolute left-1 top-1 rounded bg-black/50 px-1.5 text-[11px] font-bold text-white">{i + 1}</span>
                                    <button type="button" onClick={() => removeImage(i)} aria-label={`${i + 1}번 이미지 삭제`} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white">✕</button>
                                    <MoveButtons index={i} last={images.length - 1} onMove={moveImage} />
                                </div>
                            ))}
                            {images.length < CAROUSEL_MAX_IMAGES &&
                                <button type="button" onClick={() => openFilePicker()} className={`flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600 ${CAROUSEL_RATIOS.find(r => r.value === ratio)?.aspect}`}>
                                    <span className="text-2xl leading-none">+</span>
                                    <span className="text-[11px] font-semibold">이미지 추가</span>
                                </button>
                            }
                        </div>
                    </div>
                </section>
                : <section className="flex flex-col gap-3">
                    <div>
                        <p className="text-sm font-bold">장수</p>
                        <div className="mt-2 flex gap-2">
                            {[2, 4, 6].map(n => (
                                <button key={n} type="button" aria-pressed={count === n} onClick={() => handleChangeCount(n)} className={SEGMENT(count === n)}>{n}장</button>
                            ))}
                        </div>
                    </div>
                    {ALBUM_LAYOUTS[count].length > 1 &&
                        <div>
                            <p className="text-sm font-bold">배치</p>
                            <div className="mt-2 grid grid-cols-3 items-start gap-2 sm:grid-cols-4">
                                {ALBUM_LAYOUTS[count].map(l => (
                                    <button
                                        key={l.value}
                                        type="button"
                                        aria-pressed={layoutValue === l.value}
                                        onClick={() => setLayoutValue(l.value)}
                                        className={`flex flex-col gap-2 rounded-xl p-2.5 ring-1 transition ${layoutValue === l.value ? 'bg-gray-50 ring-2 ring-gray-900' : 'ring-gray-200 hover:bg-gray-50'}`}
                                    >
                                        {/* 세로 4컷은 그림이 길어서 선택 카드에서는 절반 너비로 */}
                                        <div className={l.value === 'STRIP' ? 'mx-auto w-1/2' : 'w-full'}><LayoutDiagram layout={{ ...l, slotCount: count }} /></div>
                                        <span className="text-[11px] font-semibold text-gray-700">{l.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    }
                    <div>
                        <div className="flex items-end justify-between gap-2">
                            <p className="text-sm font-bold">이미지 <span className="font-normal text-gray-400">칸을 눌러 사진을 채워 주세요 (여러 장 고르면 빈 칸에 순서대로) · 끌어서 또는 ◀▶ 로 자리 바꾸기</span></p>
                            {albumImages.filter(Boolean).length < count &&
                                <button type="button" onClick={fillSamples} className="shrink-0 rounded-full border border-gray-300 px-3 py-1 text-[12px] font-semibold text-gray-700 hover:bg-gray-50">
                                    빈 칸 채우기
                                </button>
                            }
                        </div>
                        <div className="mt-2 rounded-xl bg-gray-50 p-3">
                            <LayoutDiagram layout={layout} images={images} size="lg" onSlotClick={openFilePicker} onRemove={removeImage} onMove={moveImage} dragProps={dragProps} />
                        </div>
                    </div>
                </section>
            }

            <section>
                <p className="text-sm font-bold">테마 <span className="font-normal text-gray-400">기본은 피드형, 나머지는 일기장 스타일</span></p>
                <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-7">
                    {IMAGE_MODULE_THEMES.map(t => (
                        <button key={t.value} type="button" aria-pressed={theme === t.value} onClick={() => setTheme(t.value)} className={`flex flex-col items-center gap-1.5 rounded-xl p-1.5 ring-1 transition ${theme === t.value ? 'ring-2 ring-gray-900' : 'ring-gray-200 hover:bg-gray-50'}`}>
                            {/* 종이 + 테이프 + 사진 + 글줄 */}
                            <span className="relative flex aspect-[3/4] w-full flex-col gap-[3px] rounded-md p-1.5 pt-2.5 shadow-sm ring-1 ring-black/5" style={{ background: t.paper }}>
                                {t.tape && <span className="absolute left-1/2 top-0.5 h-1.5 w-1/2 -translate-x-1/2 -rotate-3" style={{ background: t.tape }} />}
                                <span className="w-full flex-1 rounded-[2px] bg-gray-300" style={t.tape ? { outline: '2px solid #fff' } : undefined} />
                                <span className="h-[3px] w-full rounded-full" style={{ background: t.line }} />
                                <span className="h-[3px] w-2/3 rounded-full" style={{ background: t.line }} />
                            </span>
                            <span className="text-[11px] font-semibold text-gray-700">{t.label}</span>
                        </button>
                    ))}
                </div>
            </section>

            <section className="flex flex-col gap-3">
                <label className="flex flex-col gap-1">
                    <span className="text-sm font-bold">제목 <span className="font-normal text-gray-400">선택</span></span>
                    <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={50} placeholder="예: 가을 신상 모음" className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-gray-900" />
                </label>
                <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between text-sm font-bold">
                        <span>설명 <span className="font-normal text-gray-400">사진 아래에 글 표시</span></span>
                        <Switch size="small" color="success" checked={showDescription} onChange={(e) => setShowDescription(e.target.checked)} inputProps={{ 'aria-label': '설명 표시' }} />
                    </div>
                    {showDescription &&
                        <>
                            <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} rows={4} placeholder="예: 바람이 선선해진 날, 오랜만에 한강에 다녀왔다." className="resize-y rounded-lg border border-gray-300 px-3 py-2.5 text-sm leading-relaxed outline-none focus:border-gray-900" />
                            <span className="self-end text-[12px] tabular-nums text-gray-400">{description.length}/500</span>
                            <div className="mt-2 rounded-xl bg-gray-50 p-4">
                                <TextStyleFields
                                    text={description}
                                    style={descStyle}
                                    onChange={(patch) => setDescStyle(prev => ({ ...prev, ...patch }))}
                                    showSize={false}
                                    showPreview={false}
                                    fonts={DESC_FONTS}
                                    defaultColorLabel="테마 기본"
                                />
                            </div>
                        </>
                    }
                </div>
                <label className="flex flex-col gap-1">
                    <span className="text-sm font-bold">링크 <span className="font-normal text-gray-400">선택 · 넣으면 &lsquo;바로가기&rsquo; 버튼</span></span>
                    <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://" className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-gray-900" />
                </label>
                {categories.length > 0 &&
                    <label className="flex flex-col gap-1">
                        <span className="text-sm font-bold">카테고리</span>
                        <select value={linkCategoryId} onChange={(e) => setLinkCategoryId(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-gray-900">
                            <option value="">전체</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </label>
                }
            </section>

            <button type="submit" className="rounded-full bg-gray-900 py-3.5 text-[15px] font-bold text-white hover:bg-gray-700">
                {submitLabel}
            </button>
        </form>
    );
}
